import {useQuery} from "@tanstack/react-query";
import {CheckIcon, Loader2Icon} from "lucide-react";

import {plansList} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";

import {formatGB, formatMB, formatPrice} from "@/lib/format";

export const PlansList = () => {
  const query = useQuery({
    queryKey: ["plans", "list"],
    queryFn: async () => {
      const {data, error} = await plansList();
      if (error) throw error;
      return (data?.items ?? []).slice().sort((a, b) => a.display_order - b.display_order);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Plans</h1>
        <p className="text-sm text-muted-foreground">
          Available plans for new servers in your organization.
        </p>
      </div>

      {query.isLoading ? (
        <div className="flex items-center justify-center p-12 text-muted-foreground">
          <Loader2Icon className="size-5 animate-spin" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-destructive">Failed to load plans.</p>
      ) : (query.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            No plans available right now.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {query.data!.map((plan) => (
            <Card key={plan.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {plan.description ?? plan.slug}
                    </CardDescription>
                  </div>
                  <Badge variant="muted">{plan.hypervisor_kind || "kvm"}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-2xl font-semibold tracking-tight">
                  {formatPrice(plan.price_cents, plan.currency)}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">/ mo</span>
                </div>
                <ul className="space-y-1.5 text-sm">
                  <Feature text={`${plan.vcpus} vCPU`} />
                  <Feature text={`${formatMB(plan.memory_mb)} memory`} />
                  <Feature text={`${formatGB(plan.disk_gb)} disk`} />
                  {plan.bandwidth_gb ? (
                    <Feature text={`${plan.bandwidth_gb} GB bandwidth`} />
                  ) : null}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

function Feature({text}: {text: string}) {
  return (
    <li className="flex items-center gap-2">
      <CheckIcon className="size-4 text-emerald-500" /> {text}
    </li>
  );
}
